/**
 * Việc thuần của "Nghỉ đột xuất" (đợt 3 · 3.4, ADR-092) — tách khỏi giao diện để thử bằng đơn vị:
 * chọn câu cho dải đỏ, danh sách người còn cho nghỉ được, ghép dòng của máy chủ với lịch trong bảng ngày,
 * và mã lỗi nào là "cuối" (bỏ khoá chống lặp theo ý định) hay "tạm" (giữ khoá, bấm lại an toàn).
 */
import {
  ApiFailure,
  type DayAppointment,
  type DongLichVuong,
  type NghiTrongNgay,
  type NgoaiCaTrongNgay,
  type StaffColumn,
} from './api.ts'
import { nhanNgayNgan } from './ngay.ts'
import { T } from './text/vi.ts'

/** Số lịch còn cần xử lý của MỘT người nghỉ. */
function demLich(n: NghiTrongNgay): number {
  return n.lichCanXuLy.length
}

/** Những người nghỉ trong ngày mà còn lịch cần xử lý. */
export function nguoiConLichVuong(nghi: readonly NghiTrongNgay[]): NghiTrongNgay[] {
  return nghi.filter((n) => demLich(n) > 0)
}

/** Tổng số lịch cần xử lý (một lịch chỉ thuộc một người nghỉ — máy chủ chọn dòng vướng đầu tiên). */
export function tongLichCanXuLy(nghi: readonly NghiTrongNgay[]): number {
  return nghi.reduce((s, n) => s + demLich(n), 0)
}

/** Số lịch ngoài ca / ngoài giờ mở cửa / của người đã nghỉ việc (BK-2, ADR-096). */
export function tongLichNgoaiCa(ngoaiCa: readonly NgoaiCaTrongNgay[]): number {
  return ngoaiCa.reduce((s, n) => s + n.lichCanXuLy.length, 0)
}

/**
 * Câu của dải đỏ ở đầu màn Lịch. `null` = không có gì cần xử lý (dải ẩn).
 * Một người: có tên; nhiều người: một câu gộp. Ngày khác hôm nay: ghi rõ ngày.
 * Có thêm lịch ngoài ca (`ngoaiCa`): chỉ có loại đó → "n lịch không có người làm"; cả hai loại → một câu gộp tổng.
 * Chỉ có người nghỉ → chữ cũ giữ nguyên.
 */
export function cauDaiLichVuong(
  nghi: readonly NghiTrongNgay[],
  date: string,
  homNay: string,
  ngoaiCa: readonly NgoaiCaTrongNgay[] = [],
): string | null {
  const con = nguoiConLichVuong(nghi)
  const tong = tongLichCanXuLy(con)
  const ngoai = tongLichNgoaiCa(ngoaiCa)
  if (tong === 0 && ngoai === 0) return null
  // K2 (ADR-111): chi nhánh đóng cửa — chỉ còn việc gọi khách đổi ngày, câu nói thẳng điều đó.
  if (tong === 0 && ngoaiCa.length > 0 && ngoaiCa.every((n) => n.lyDo === 'tiem-nghi')) {
    return T.offBandClosed(ngoai)
  }
  if (ngoai > 0 && tong > 0) return T.offBandAll(tong + ngoai)
  if (ngoai > 0) return T.offBandOutside(ngoai)
  const dau = con[0]
  if (con.length === 1 && dau !== undefined) {
    return date === homNay
      ? T.offBandOne(dau.ten, tong)
      : T.offBandOneDay(dau.ten, nhanNgayNgan(date), tong)
  }
  return T.offBandMany(con.length, tong)
}

/** Người còn cho nghỉ hôm nay được: có ca trong ngày (cột của bảng) và chưa nghỉ. */
export function nguoiChoNghiDuoc(
  staff: readonly StaffColumn[],
  nghi: readonly NghiTrongNgay[],
): { id: string; ten: string }[] {
  const da = new Set(nghi.map((n) => n.staffId))
  return staff.filter((s) => !da.has(s.staffId)).map((s) => ({ id: s.staffId, ten: s.fullName }))
}

/**
 * v0.22.3 B1: chi nhánh đang xem chỉ còn ĐÚNG MỘT người có ca chưa nghỉ → cho người đó nghỉ là cả tiệm nghỉ. Mở "Nghỉ đột xuất"
 * lúc này bỏ bước chọn tên, hỏi thẳng "Cả tiệm nghỉ hôm nay?" (máy chủ vẫn là chốt: `KHONG_CON_NGUOI_NHAN`).
 */
export function chiConMotNguoi(
  staff: readonly StaffColumn[],
  nghi: readonly NghiTrongNgay[],
): boolean {
  return nguoiChoNghiDuoc(staff, nghi).length === 1
}

/** Bước mở đầu của tờ "Nghỉ đột xuất": từ nút bấm mà chỉ một người thì vào thẳng `ca-tiem`; từ dải đỏ giữ danh sách. */
export function buocMoDau(
  buocDau: 'chon' | 'ds',
  staff: readonly StaffColumn[],
  nghi: readonly NghiTrongNgay[],
): 'chon' | 'ds' | 'ca-tiem' {
  return buocDau === 'chon' && chiConMotNguoi(staff, nghi) ? 'ca-tiem' : buocDau
}

/**
 * G-N3: tiêu đề tờ danh sách. Vừa bấm "Đóng cửa tiệm hôm nay" thì nói đúng việc đã làm — "Còn 0 lịch cần xử lý" chỉ làm chủ tiệm
 * tưởng chưa xong.
 */
export function tieuDeDanhSach(x: { daDongCua: boolean; tongLich: number }): string {
  return x.daDongCua ? T.offShopClosedTitle : T.offListTitle(x.tongLich)
}

/** G-N3: câu khi danh sách rỗng; `null` = không nói gì (vừa đóng cửa tiệm — tiêu đề đã xác nhận, "không có lịch nào cần chuyển" thừa). */
export function tieuRongDanhSach(x: {
  daDongCua: boolean
  tungCoViec: boolean
  tenCuoi: string
}): string | null {
  if (x.daDongCua) return null
  return x.tungCoViec && x.tenCuoi !== '' ? T.offAllDone(x.tenCuoi) : T.offNothing
}

/**
 * L-N1: máy chủ báo người này là người nhận khách cuối ở những chi nhánh nào (`chiNhanhIds`). Tờ "Cả tiệm nghỉ hôm nay?" chỉ
 * được đóng đúng chi nhánh ĐANG XEM khi nó nằm trong danh sách đó (hoặc máy chủ cũ không báo); nếu khác thì chỉ nói rõ chi nhánh
 * nào, không đưa nút đóng — đóng nhầm chi nhánh đang xem là đóng oan một chi nhánh còn người làm.
 */
export function huongCaTiem(
  chiNhanhIds: readonly string[],
  dangXem: string,
): 'dong-dang-xem' | 'chi-nhanh-khac' {
  return chiNhanhIds.length === 0 || chiNhanhIds.includes(dangXem)
    ? 'dong-dang-xem'
    : 'chi-nhanh-khac'
}

/** Máy chủ từ chối vì người này là người nhận khách cuối cùng của một ngày đang mở cửa (hai người trở lên ở bảng nhưng ca ở chi nhánh khác…). */
export function laLoiCaTiem(e: unknown): boolean {
  return e instanceof ApiFailure && e.code === 'KHONG_CON_NGUOI_NHAN'
}

/**
 * Nút "Nghỉ đột xuất" trên màn Lịch: chủ / quản lý (`xep-ca`), chỉ hôm nay, còn ít nhất một người chưa nghỉ, và (B9)
 * hôm nay tiệm CHƯA đóng cửa — đóng rồi thì nghỉ một người không còn ý nghĩa; mở lại ở tờ "Cả tiệm nghỉ" hoặc Cấu hình.
 */
export function nutNghiDotXuatHien(x: {
  coXepCa: boolean
  laHomNay: boolean
  staff: readonly StaffColumn[]
  nghi: readonly NghiTrongNgay[]
  dongCua?: { lyDo: string | null } | null | undefined
}): boolean {
  return (
    x.coXepCa &&
    x.laHomNay &&
    (x.dongCua ?? null) === null &&
    nguoiChoNghiDuoc(x.staff, x.nghi).length > 0
  )
}

/** Nhân viên chọn được ở tờ Đổi giờ: bỏ người đang nghỉ (chọn họ thì không có khe nào). */
export function nhanVienDoiGio(
  staff: readonly StaffColumn[],
  nghi: readonly NghiTrongNgay[],
): { id: string; ten: string }[] {
  return nguoiChoNghiDuoc(staff, nghi)
}

export interface DongHienThi {
  dong: DongLichVuong
  lich: DayAppointment
  tenNguoiNghi: string
}

/**
 * Ghép từng dòng của `GET /lich-vuong` với lịch trong bảng ngày (đã che số theo quyền). Dòng không có trong bảng
 * (vừa đổi / bảng cũ hơn danh sách) → bỏ qua tới lần tải sau.
 */
export function ghepDong(
  ds: readonly DongLichVuong[],
  lich: readonly DayAppointment[],
  nghi: readonly NghiTrongNgay[],
  staff: readonly StaffColumn[],
  ngoaiCa: readonly NgoaiCaTrongNgay[] = [],
): DongHienThi[] {
  const out: DongHienThi[] = []
  for (const dong of ds) {
    const a = lich.find((x) => x.appointmentId === dong.appointmentId)
    if (a === undefined) continue
    const ten =
      nghi.find((n) => n.staffId === dong.staffId)?.ten ??
      ngoaiCa.find((n) => n.staffId === dong.staffId)?.ten ??
      staff.find((s) => s.staffId === dong.staffId)?.fullName ??
      ''
    out.push({ dong, lich: a, tenNguoiNghi: ten })
  }
  return out
}

/**
 * Mã lỗi CUỐI: máy chủ đã trả lời dứt khoát cho yêu cầu này → bỏ khoá chống lặp theo ý định (lần bấm sau là việc
 * mới). Mạng chập chờn / hết giờ / 429 / 5xx KHÔNG nằm đây: giữ khoá để bấm lại không thành hai lần chuyển
 * (bài học 126). Mã lạ → coi là tạm (giữ khoá) cho an toàn.
 */
const MA_CUOI = new Set([
  'SLOT_NOT_AVAILABLE',
  'SLOT_TAKEN',
  'LICH_VUA_DOI',
  'VALIDATION',
  'NOT_FOUND',
  'KHONG_CON_NGUOI_NHAN',
])
export function laLoiCuoi(code: string): boolean {
  return MA_CUOI.has(code)
}

/** Khoá theo ý định `(lịch, người mới)`. */
export function khoaYDinh(appointmentId: string, staffId: string): string {
  return `${appointmentId}|${staffId}`
}

/** Số lịch vướng ở chi nhánh KHÁC chi nhánh đang xem (từ phản hồi POST cho nghỉ). */
export function demChiNhanhKhac(
  lichVuong: readonly { branchId: string }[],
  branchId: string,
): number {
  return lichVuong.filter((x) => x.branchId !== branchId).length
}
